<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import {
  faBackpack,
  faClipboardListCheck,
  faCookie,
  faCopyright,
  faShieldKeyhole,
  faUniversalAccess,
  faUserHeadset
} from '@fortawesome/duotone-regular-svg-icons'
import { useAuthStore } from '~/stores/auth'
import { useAppColor } from '~/composables/useAppColor'
import AcoLogo from '~/components/utils/AcoLogo.vue'
import Button from '~/components/ui/Button.vue'
import FeedbackButton from '~/components/feedback/FeedbackButton.vue'

const { t } = useI18n()
const { isTeacher } = storeToRefs(useAuthStore())
const { bgColor } = useAppColor()

const webshopUrl = import.meta.env.VITE_WEBSHOP_SITEURL
const supportUrl = import.meta.env.VITE_WEBSHOP_SUPPORT
const whitelistUrl = `${import.meta.env.VITE_AUNIVERS_SITEURL}/hvitlisting`
const privacyUrl = `${import.meta.env.VITE_AUNIVERS_SITEURL}/personvern`
const accessibilityUrl = `${import.meta.env.VITE_AUNIVERS_SITEURL}/uu`
const copyrightUrl = `${import.meta.env.VITE_AUNIVERS_SITEURL}/rettigheter`
const cookiesUrl = `${import.meta.env.VITE_AUNIVERS_SITEURL}/cookies`

const showFeedbackButton = computed(() => isTeacher.value)

const shade = computed(() => {
  const name = (bgColor.value || '').toLowerCase()
  if (name.includes('white'))
    return 'dark'
  const matches = /-(?:5|10|20)(?=\)?$)/.test(name)
  return matches ? 'dark' : 'light'
})

const logoColor = computed(() => shade.value === 'dark' ? 'text-green-50' : 'text-green-5')
</script>

<template>
  <footer :style="{ background: bgColor }" class="z-10 m-auto w-full space-y-4 p-8">
    <AcoLogo :class="logoColor" class="xs:mx-auto w-38" />
    <ul class="xs:flex-row flex flex-col flex-wrap justify-center gap-2 text-sm">
      <li>
        <Button
          :href="supportUrl"
          :shade
          color="gray"
          size="lg"
          target="_blank"
          variant="ghost"
        >
          <font-awesome-icon :icon="faUserHeadset" />
          {{ t('footer.customerService') }}
        </Button>
      </li>
      <li>
        <Button
          :href="whitelistUrl"
          :shade
          color="gray"
          size="lg"
          target="_blank"
          variant="ghost"
        >
          <font-awesome-icon :icon="faClipboardListCheck" />
          {{ t('footer.whitelist') }}
        </Button>
      </li>
      <li>
        <Button
          :href="privacyUrl"
          :shade
          color="gray"
          size="lg"
          target="_blank"
          variant="ghost"
        >
          <font-awesome-icon :icon="faShieldKeyhole" />
          {{ t('footer.privacy') }}
        </Button>
      </li>
      <li>
        <Button
          :href="accessibilityUrl"
          :shade
          color="gray"
          size="lg"
          target="_blank"
          variant="ghost"
        >
          <font-awesome-icon :icon="faUniversalAccess" />
          {{ t('footer.accessibility') }}
        </Button>
      </li>
      <li>
        <Button
          :href="webshopUrl"
          :shade
          color="gray"
          size="lg"
          target="_blank"
          variant="ghost"
        >
          <font-awesome-icon :icon="faBackpack" />
          {{ t('footer.webshop') }}
        </Button>
      </li>
      <li>
        <Button
          :href="copyrightUrl"
          :shade
          color="gray"
          size="lg"
          target="_blank"
          variant="ghost"
        >
          <font-awesome-icon :icon="faCopyright" />
          {{ t('footer.copyright') }}
        </Button>
      </li>
      <li>
        <Button
          :href="cookiesUrl"
          :shade
          color="gray"
          size="lg"
          target="_blank"
          variant="ghost"
        >
          <font-awesome-icon :icon="faCookie" />
          {{ t('footer.cookies') }}
        </Button>
      </li>
      <li v-if="showFeedbackButton">
        <FeedbackButton
          :shade
          color="gray"
          size="lg"
          variant="ghost"
        >
          {{ t('reportFeedback.button') }}
        </FeedbackButton>
      </li>
    </ul>
  </footer>
</template>
